import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { Service } from "../service";

import { CREATE, MUTATION_KEYS, QUERY_KEYS } from "./common";

import { combine, throwify } from "~/shared/fp";
import type { GetMutationOptions, InferMutationGenerics } from "~/shared/tanstack-query";

type UseCreateGenerics = InferMutationGenerics<Service[typeof CREATE]>;
type UseCreateOptions = GetMutationOptions<UseCreateGenerics>;

function useCreate(
  service: Service,
  options?: Omit<UseCreateOptions, "mutationKey" | "mutationFn">,
  queryClient?: QueryClient,
) {
  const fallbackQueryClient = useQueryClient();
  const queryClientToUse = queryClient ?? fallbackQueryClient;
  const { onSuccess } = options ?? {};

  return useMutation(
    {
      ...options,
      mutationKey: MUTATION_KEYS[CREATE],
      mutationFn: async (params) => {
        return throwify(await service.create(params)());
      },
      onSuccess: combine([
        () =>
          void queryClientToUse.invalidateQueries({
            queryKey: QUERY_KEYS._def,
          }),
        onSuccess,
      ]),
    } as UseCreateOptions,
    queryClient,
  );
}

export { useCreate };
